import initializers from './config/initializers'

import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Helmet, HelmetProvider } from 'react-helmet-async'
import { BrowserRouter, Route, Routes } from 'react-router-dom'
import { ProtectedRoute } from './components/ProtectedRoute'
import {
  ApplicationsListPage,
  CreateApplicationOverviewPage,
  CreateApplicationPage,
  ForgotPasswordPage,
  ForgotPasswordResetPage,
  LoginPage,
  NotFoundPage,
  OnboardingPage,
  ProfilePage,
  SignUpConfirmationPage,
  SignUpPage,
  ViewApplicationPage,
} from './pages'
import { ConfigProvider } from './utils/ConfigProvider'
import { PublicRoute } from './components/PublicRoute'
import { ErrorElement } from './pages/ErrorPage'
import { ErrorBoundaryWrapper } from './components/ErrorBoundary'
import { MonitorProvider } from './hooks'
import { useGetSpaConfig } from './hooks/useGetSpaConfig'
import { CrystalProvider } from '@applyboard/crystal-ui'
import { LoginCallbackRedirect } from './components/LoginCallbackRedirect/LoginCallbackRedirect'

initializers.init()

const queryClient = new QueryClient()

function CrystalWrapper({ children }: { children: React.ReactNode }) {
  const { ui, isLoading, school } = useGetSpaConfig()
  const themeColors = ui?.crystalTheme?.colors

  if (isLoading) return null

  return (
    <CrystalProvider theme={themeColors ? { colors: themeColors } : undefined}>
      <Helmet>
        <link rel="icon" href={ui?.favicon} />
        <title>{school.name ? `Apply | ${school.name}` : 'AMS'}</title>
      </Helmet>
      {children}
    </CrystalProvider>
  )
}

function App() {
  return (
    <HelmetProvider>
      <Helmet>
        <script src="https://payment.flywire.com/assets/js/checkout.js"></script>
      </Helmet>
      <QueryClientProvider client={queryClient}>
        <CrystalWrapper>
          <MonitorProvider>
            <ErrorBoundaryWrapper>
              <BrowserRouter>
                <ConfigProvider>
                  <Routes>
                    <Route element={<ProtectedRoute />} errorElement={<ErrorElement />}>
                      <Route
                        path="/"
                        element={<ApplicationsListPage />}
                        errorElement={<ErrorElement />}
                      />
                      <Route
                        path="/onboarding"
                        element={<OnboardingPage />}
                        errorElement={<ErrorElement />}
                      />
                      <Route
                        path="/profile"
                        element={<ProfilePage />}
                        errorElement={<ErrorElement />}
                      />
                      <Route
                        path="/applications/overview"
                        element={<CreateApplicationOverviewPage />}
                        errorElement={<ErrorElement />}
                      />
                      <Route
                        path="/applications/new"
                        element={<CreateApplicationPage />}
                        errorElement={<ErrorElement />}
                      />
                      <Route
                        path="/applications/:id"
                        element={<ViewApplicationPage />}
                        errorElement={<ErrorElement />}
                      />
                      <Route
                        path="/signup/confirm"
                        element={<SignUpConfirmationPage isOktaSignUp={true} />}
                        errorElement={<ErrorElement />}
                      />
                    </Route>
                    <Route element={<PublicRoute />} errorElement={<ErrorElement />}>
                      <Route
                        path="/signup/:username/confirm"
                        element={<SignUpConfirmationPage />}
                        errorElement={<ErrorElement />}
                      />

                      <Route
                        path="/signup"
                        element={<SignUpPage />}
                        errorElement={<ErrorElement />}
                      />
                      <Route
                        path="/login"
                        element={<LoginPage />}
                        errorElement={<ErrorElement />}
                      />
                      <Route
                        path="/forgot-password/:username/reset"
                        element={<ForgotPasswordResetPage />}
                        errorElement={<ErrorElement />}
                      />
                      <Route
                        path="/forgot-password"
                        element={<ForgotPasswordPage />}
                        errorElement={<ErrorElement />}
                      />
                    </Route>
                    <Route
                      path="/login/callback"
                      element={<LoginCallbackRedirect />}
                      errorElement={<ErrorElement />}
                    />

                    <Route path="*" element={<NotFoundPage />} errorElement={<ErrorElement />} />
                  </Routes>
                </ConfigProvider>
              </BrowserRouter>
            </ErrorBoundaryWrapper>
          </MonitorProvider>
        </CrystalWrapper>
      </QueryClientProvider>
    </HelmetProvider>
  )
}

export default App
